import {
  Button,
  SelectOptionType,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import RemovableChip from '../../../../components/chip/RemovableChip';
import ChipMultiSelect from '../../../../components/form/ChipMultiSelect';
import { SectionTitle } from '../../../../components/text';
import { TranslationsKeys } from '../../../../locales/translations';
import { saveApprovers } from '../api';
import { useHomologationFormLoader } from './Loaders/HomologationFormLoader';

interface FormValues {
  approvers: SelectOptionType[];
}

export default function ApproverUsersSection() {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_HOMOLOGATION);
  const { data } = useHomologationFormLoader();
  const { control, setValue, watch } = useForm<FormValues>({
    defaultValues: { approvers: [] },
  });
  const [saving, setSaving] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  const approvers = watch('approvers') ?? [];

  useEffect(() => {
    if (data?.savedApprovers) {
      setValue('approvers', data.savedApprovers);
    }
  }, [data, setValue]);

  const handleRemove = useCallback(
    (index: number) => {
      showDialogConfirm({
        icon: {
          name: 'information',
          color: 'primary',
        },
        title: t('disableConfirmTitle'),
        subtitle: t('disableConfirmDescription'),
        btnConfirm: {
          text: t('disableButton'),
          onConfirm: () => {
            const current = [...approvers];
            current.splice(index, 1);
            setValue('approvers', current);
          },
        },
        btnClose: {
          text: t('cancelButton'),
        },
      });
    },
    [approvers, setValue, t],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const emails = approvers.map((opt) => String(opt.value));
      await saveApprovers(emails);
      addToast({
        icon: 'success',
        color: 'success',
        text: t('saveSuccess'),
      });
    } catch (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('saveError'),
      });
    } finally {
      setSaving(false);
    }
  }, [approvers, t]);

  return (
    <div>
      <SectionTitle text={t('title')} />
      <p className="text-muted mb-3">{t('description')}</p>

      <Row className="mb-4">
        <Col md={12}>
          <ChipMultiSelect
            name="approvers"
            label={t('searchLabel')}
            control={control}
            options={data?.userOptions ?? []}
            placeholder={t('searchPlaceholder')}
            hideChips
          />
        </Col>
      </Row>

      {approvers.length > 0 && (
        <>
          <SectionTitle text={t('selectedUsersTitle')} />
          <div
            className="w-full mb-3 mt-2 d-flex flex-wrap align-items-center justify-content-start"
            style={{ gap: 5 }}
          >
            {approvers.map((approver, index) => (
              <RemovableChip
                key={String(approver.value)}
                value={index}
                onRemove={handleRemove}
              >
                {approver.label}
              </RemovableChip>
            ))}
          </div>
        </>
      )}

      <Row>
        <Col className="d-flex justify-content-end">
          <Button
            text={t('saveButton')}
            onClick={handleSave}
            loading={saving}
          />
        </Col>
      </Row>
    </div>
  );
}
